import { useEffect } from 'react';

import { useSalesV2CheckOfferEligibilityV2 } from '@eneco/dc/hooks/emsales/SalesV2';
import useDC from '@eneco/dc/useDC';
import { useTracking } from '@eneco/tracking';
import { OfferType } from '@monorepo-types/dc/emsales';

import { FlowContext } from '../types';
import { useFlowHooks } from './FlowProvider';

// Each flow extends FlowContext with its own `isSuitable` field, which is all this hook needs.
interface SuitabilityFlowContext extends FlowContext {
  isSuitable?: boolean;
}

type CustomerProfile = Pick<
  ReturnType<(typeof import('@eneco/hooks/profile'))['useCustomerProfileAccount']>,
  'currentAccount' | 'isLoading'
>;

// Shared by flows that must check offer eligibility for the current account and redirect to
// /nog-even-geduld/ when the customer turns out not to be suitable for the given offerType.
export const useOfferEligibilityRedirect = (
  { currentAccount, isLoading: isProfileLoading }: CustomerProfile,
  offerType: OfferType,
  flowName?: string,
) => {
  const { trackFunnelCompleted } = useTracking();
  const { customerId } = useDC();
  const { useFlowActorRef, useFlowSelector } = useFlowHooks<SuitabilityFlowContext>();
  const { send: sendToFlowMachine } = useFlowActorRef();
  const isSuitable = useFlowSelector(state => state.context.isSuitable);
  const currentRoute = useFlowSelector(state => state.context.path);

  const {
    send: checkOfferEligibility,
    data: eligibilityData,
    isLoading: isEligibilityLoading,
    isIdle: isEligibilityIdle,
    isSuccess: isEligibilitySuccess,
    isError: isEligibilityError,
  } = useSalesV2CheckOfferEligibilityV2();

  // Requires both customerId and accountId, as either can still be null right after login (see DigitalCoreProvider).
  const isAccountReady = !isProfileLoading && !!customerId && !!currentAccount?.accountId;

  useEffect(() => {
    if (!isAccountReady) {
      return;
    }

    checkOfferEligibility({ offerType });
  }, [checkOfferEligibility, isAccountReady, offerType]);

  const shouldRedirect = isEligibilitySuccess && eligibilityData?.isEligible === false;

  const isLoading = !!isProfileLoading || isEligibilityIdle || isEligibilityLoading;

  // Step 1: synchronize eligibility in context so the basket persists it before navigation.
  useEffect(() => {
    if (isLoading || !isEligibilitySuccess || isSuitable === !shouldRedirect) {
      return;
    }

    sendToFlowMachine({
      type: 'UPDATE_VALUES',
      values: { isSuitable: !shouldRedirect },
    });
  }, [isEligibilitySuccess, isLoading, isSuitable, sendToFlowMachine, shouldRedirect]);

  // Step 2: navigate to nog-even-geduld — FlowRouter picks up the path change and does router.push.
  useEffect(() => {
    if (!shouldRedirect || isSuitable !== false || currentRoute === '/nog-even-geduld/') {
      return;
    }

    trackFunnelCompleted({ funnel: flowName });
    sendToFlowMachine({ type: 'UPDATE_ROUTE', values: { path: '/nog-even-geduld/' } });
  }, [currentRoute, flowName, isSuitable, sendToFlowMachine, shouldRedirect, trackFunnelCompleted]);

  return {
    isLoading,
    shouldRedirect,
    // Surfaced so callers can fail closed instead of treating a failed check as eligible.
    isError: isEligibilityError,
  };
};
